import { encodeFunctionData } from "viem";

import { POOL_SWAP_TEST_ABI } from "../contracts/v4/pool-swap-test";
import { V2_ROUTER_ABI, V3_SWAP_ROUTER_ABI } from "../lib/abis";
import {
  deadline,
  MAX_SQRT_PRICE_LIMIT,
  MAX_UINT256,
  MIN_SQRT_PRICE_LIMIT,
  sortTokens,
  ZERO_ADDRESS,
} from "../lib/constants";
import { approveToken } from "../lib/helpers";

import type { DeployContext } from "../deployer/deploy-utils";
import type { MockTokenAddresses, UniswapDeployment } from "../deployer/types";
import type { Address, Hex } from "viem";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SwapRecord {
  protocol: "v2" | "v3" | "v4";
  tokenIn: Address;
  tokenOut: Address;
  /** Intended swap amount in token-native decimals */
  amountIn: bigint;
  /** Swap direction for narrative (e.g., "buy ETH with USDC") */
  description: string;
}

export interface SeedSwapsOptions {
  /** Optional accounts to rotate through for swaps (defaults to deployer only) */
  accounts?: Address[];
}

export interface SeedSwapsResult {
  totalSwaps: number;
  swaps: SwapRecord[];
  durationMs: number;
}

// ---------------------------------------------------------------------------
// Swap Definitions
// ---------------------------------------------------------------------------

interface SwapDef {
  protocol: "v2" | "v3" | "v4";
  tokenIn: Address;
  tokenOut: Address;
  amountIn: bigint;
  description: string;
  /** V3 fee in Uniswap units (500, 3000, 10000) */
  v3Fee?: number;
  /** V4 pool config */
  v4Config?: { fee: number; tickSpacing: number };
}

function generateSwapDefs(tokens: MockTokenAddresses): SwapDef[] {
  const defs: SwapDef[] = [];

  // V2 swaps (10): basic constant-product AMM
  const v2Swaps: [Address, Address, bigint, string][] = [
    [tokens.usdc, tokens.weth, 1000n * 10n ** 6n, "buy ETH with USDC"],
    [tokens.weth, tokens.usdc, 500n * 10n ** 12n, "sell ETH for USDC"],
    [tokens.usdt, tokens.weth, 1000n * 10n ** 6n, "buy ETH with USDT"],
    [tokens.weth, tokens.usdt, 500n * 10n ** 12n, "sell ETH for USDT"],
    [tokens.dai, tokens.weth, 1000n * 10n ** 18n, "buy ETH with DAI"],
    [tokens.weth, tokens.dai, 500n * 10n ** 12n, "sell ETH for DAI"],
    [tokens.usdc, tokens.dai, 500n * 10n ** 6n, "swap USDC to DAI"],
    [tokens.dai, tokens.usdc, 500n * 10n ** 18n, "swap DAI to USDC"],
    [tokens.usdc, tokens.weth, 2000n * 10n ** 6n, "buy ETH with USDC (2)"],
    [tokens.weth, tokens.usdc, 1000n * 10n ** 12n, "sell ETH for USDC (2)"],
  ];

  for (const [tokenIn, tokenOut, amountIn, description] of v2Swaps) {
    defs.push({ protocol: "v2", tokenIn, tokenOut, amountIn, description });
  }

  // V3 swaps (15): concentrated liquidity with various fee tiers
  const v3Swaps: [Address, Address, bigint, string, number][] = [
    [tokens.usdc, tokens.weth, 500n * 10n ** 6n, "V3 buy ETH (5bps)", 500],
    [tokens.weth, tokens.usdc, 250n * 10n ** 12n, "V3 sell ETH (5bps)", 500],
    [
      tokens.usdc,
      tokens.weth,
      1000n * 10n ** 6n,
      "V3 buy ETH (5bps, large)",
      500,
    ],
    [tokens.weth, tokens.usdt, 250n * 10n ** 12n, "V3 sell ETH for USDT", 3000],
    [tokens.usdt, tokens.weth, 500n * 10n ** 6n, "V3 buy ETH with USDT", 3000],
    [tokens.weth, tokens.dai, 250n * 10n ** 12n, "V3 sell ETH for DAI", 3000],
    [tokens.dai, tokens.weth, 500n * 10n ** 18n, "V3 buy ETH with DAI", 3000],
    [
      tokens.usdc,
      tokens.dai,
      1000n * 10n ** 6n,
      "V3 stablecoin swap (1bps)",
      100,
    ],
    [
      tokens.dai,
      tokens.usdc,
      1000n * 10n ** 18n,
      "V3 stablecoin swap (1bps, rev)",
      100,
    ],
    [tokens.weth, tokens.wbtc, 250n * 10n ** 12n, "V3 sell ETH for WBTC", 3000],
    [tokens.wbtc, tokens.weth, 1n * 10n ** 7n, "V3 buy ETH with WBTC", 3000],
    [
      tokens.usdc,
      tokens.weth,
      500n * 10n ** 6n,
      "V3 buy ETH (accumulation)",
      500,
    ],
    [
      tokens.weth,
      tokens.usdc,
      250n * 10n ** 12n,
      "V3 sell ETH (distribution)",
      500,
    ],
    [tokens.usdt, tokens.dai, 500n * 10n ** 6n, "V3 stablecoin rotation", 100],
    [
      tokens.dai,
      tokens.usdt,
      500n * 10n ** 18n,
      "V3 stablecoin rotation (rev)",
      100,
    ],
  ];

  for (const [tokenIn, tokenOut, amountIn, description, v3Fee] of v3Swaps) {
    defs.push({
      protocol: "v3",
      tokenIn,
      tokenOut,
      amountIn,
      description,
      v3Fee,
    });
  }

  // V4 swaps (5): hook-enabled swaps
  const v4Config = { fee: 3000, tickSpacing: 60 };
  const v4Swaps: [Address, Address, bigint, string][] = [
    [tokens.usdc, tokens.weth, 500n * 10n ** 6n, "V4 hook swap (ETH buy)"],
    [tokens.weth, tokens.usdc, 250n * 10n ** 12n, "V4 hook swap (ETH sell)"],
    [tokens.weth, tokens.usdc, 500n * 10n ** 12n, "V4 hook swap (ETH sell 2)"],
    [
      tokens.usdc,
      tokens.weth,
      1000n * 10n ** 6n,
      "V4 hook swap (ETH buy large)",
    ],
    [tokens.weth, tokens.usdc, 250n * 10n ** 12n, "V4 hook swap (ETH sell 3)"],
  ];

  for (const [tokenIn, tokenOut, amountIn, description] of v4Swaps) {
    defs.push({
      protocol: "v4",
      tokenIn,
      tokenOut,
      amountIn,
      description,
      v4Config,
    });
  }

  return defs;
}

// ---------------------------------------------------------------------------
// V2 Swap Execution
// ---------------------------------------------------------------------------

async function executeV2Swaps(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  defs: SwapDef[],
  account?: Address,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SwapRecord[]> {
  const router = deployment.v2.router02;
  const records: SwapRecord[] = [];
  const sender = account ?? ctx.deployer;

  for (let idx = 0; idx < defs.length; idx++) {
    const def = defs[idx];
    if (!def) continue;
    const label = `V2 swap ${String(idx + 1)}/${String(defs.length)}`;
    await onAction?.(`${label} — approving`);

    // Approve tokenIn to router
    await approveToken(ctx, def.tokenIn, router, MAX_UINT256, account);

    // Execute swap
    await onAction?.(`${label} — executing`);
    const data = encodeFunctionData({
      abi: V2_ROUTER_ABI,
      functionName: "swapExactTokensForTokens",
      args: [
        def.amountIn,
        0n, // amountOutMin (devenv, accept any)
        [def.tokenIn, def.tokenOut],
        sender,
        deadline(),
      ],
    });

    try {
      await ctx.walletClient.sendTransaction({
        account: sender,
        to: router,
        data: data,
        chain: ctx.walletClient.chain,
      });

      records.push({
        protocol: "v2",
        tokenIn: def.tokenIn,
        tokenOut: def.tokenOut,
        amountIn: def.amountIn,
        description: def.description,
      });
    } catch {
      // Skip swaps that revert (e.g., pool not initialized or no liquidity)
    }
  }

  return records;
}

// ---------------------------------------------------------------------------
// V3 Swap Execution
// ---------------------------------------------------------------------------

async function executeV3Swaps(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  defs: SwapDef[],
  account?: Address,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SwapRecord[]> {
  const router = deployment.v3.router;
  const records: SwapRecord[] = [];
  const sender = account ?? ctx.deployer;

  for (let idx = 0; idx < defs.length; idx++) {
    const def = defs[idx];
    if (!def) continue;
    const label = `V3 swap ${String(idx + 1)}/${String(defs.length)}`;
    await onAction?.(`${label} — approving`);

    // Approve tokenIn to router
    await approveToken(ctx, def.tokenIn, router, MAX_UINT256, account);

    // Determine sqrtPriceLimitX96 direction
    const [token0] = sortTokens(def.tokenIn, def.tokenOut);
    const zeroForOne = def.tokenIn.toLowerCase() === token0.toLowerCase();
    const sqrtPriceLimitX96 = zeroForOne
      ? MIN_SQRT_PRICE_LIMIT
      : MAX_SQRT_PRICE_LIMIT;

    await onAction?.(`${label} — executing`);
    const data = encodeFunctionData({
      abi: V3_SWAP_ROUTER_ABI,
      functionName: "exactInputSingle",
      args: [
        {
          tokenIn: def.tokenIn,
          tokenOut: def.tokenOut,
          fee: def.v3Fee ?? 3000,
          recipient: sender,
          deadline: deadline(),
          amountIn: def.amountIn,
          amountOutMinimum: 0n,
          sqrtPriceLimitX96,
        },
      ],
    });

    try {
      await ctx.walletClient.sendTransaction({
        account: sender,
        to: router,
        data: data,
        chain: ctx.walletClient.chain,
      });

      records.push({
        protocol: "v3",
        tokenIn: def.tokenIn,
        tokenOut: def.tokenOut,
        amountIn: def.amountIn,
        description: def.description,
      });
    } catch {
      // Skip swaps that revert (e.g., pool not initialized or no liquidity)
    }
  }

  return records;
}

// ---------------------------------------------------------------------------
// V4 Swap Execution
// ---------------------------------------------------------------------------

async function executeV4Swaps(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  _tokens: MockTokenAddresses,
  defs: SwapDef[],
  account?: Address,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SwapRecord[]> {
  const poolSwapTest = deployment.v4.poolSwapTest;
  const records: SwapRecord[] = [];
  const sender = account ?? ctx.deployer;

  for (let idx = 0; idx < defs.length; idx++) {
    const def = defs[idx];
    if (!def) continue;
    const label = `V4 swap ${String(idx + 1)}/${String(defs.length)}`;
    await onAction?.(`${label} — approving`);

    // Approve tokenIn to PoolSwapTest
    await approveToken(ctx, def.tokenIn, poolSwapTest, MAX_UINT256, account);

    const [currency0, currency1] = sortTokens(def.tokenIn, def.tokenOut);
    const zeroForOne = def.tokenIn.toLowerCase() === currency0.toLowerCase();

    const config = def.v4Config ?? { fee: 3000, tickSpacing: 60 };

    const poolKey = {
      currency0,
      currency1,
      fee: config.fee,
      tickSpacing: config.tickSpacing,
      hooks: ZERO_ADDRESS,
    };

    const swapParams = {
      zeroForOne,
      amountSpecified: -def.amountIn, // negative = exact input
      sqrtPriceLimitX96: zeroForOne
        ? MIN_SQRT_PRICE_LIMIT
        : MAX_SQRT_PRICE_LIMIT,
    };

    const testSettings = {
      takeClaims: false,
      settleUsingBurn: false,
    };

    await onAction?.(`${label} — executing`);
    const data = encodeFunctionData({
      abi: POOL_SWAP_TEST_ABI,
      functionName: "swap",
      args: [poolKey, swapParams, testSettings, "0x" as Hex],
    });

    try {
      await ctx.walletClient.sendTransaction({
        account: sender,
        to: poolSwapTest,
        data: data,
        chain: ctx.walletClient.chain,
      });

      records.push({
        protocol: "v4",
        tokenIn: def.tokenIn,
        tokenOut: def.tokenOut,
        amountIn: def.amountIn,
        description: def.description,
      });
    } catch {
      // Skip swaps that revert (e.g., pool not initialized or no liquidity)
    }
  }

  return records;
}

// ---------------------------------------------------------------------------
// Block mining helper
// ---------------------------------------------------------------------------

/**
 * Mine a number of blocks via `evm_mine`.
 *
 * Called periodically during multi-account swap seeding to create
 * realistic block spacing between swaps.
 */
async function mineBlocks(ctx: DeployContext, count: number): Promise<void> {
  for (let i = 0; i < count; i++) {
    await ctx.request({ method: "evm_mine", params: [] });
  }
}

// ---------------------------------------------------------------------------
// Main Entry Point
// ---------------------------------------------------------------------------

/**
 * Execute test swaps across V2, V3, and V4 to create transaction history.
 *
 * Makes real on-chain calls:
 * - V2 Router02: `swapExactTokensForTokens()`
 * - V3 SwapRouter: `exactInputSingle()`
 * - V4 PoolSwapTest: `swap()`
 *
 * Each swap includes a token approval before execution.
 *
 * When `opts.accounts` is provided, swaps rotate through the given
 * accounts. Each account is impersonated via `anvil_impersonateAccount`
 * before its first use. Every ~3rd swap mines 1-3 blocks to create
 * realistic block spacing.
 */
export async function seedSwaps(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  tokens: MockTokenAddresses,
  opts?: SeedSwapsOptions,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeedSwapsResult> {
  const start = Date.now();
  const accounts = opts?.accounts;

  const allDefs = generateSwapDefs(tokens);

  // Multi-account mode: rotate through accounts, mining blocks periodically
  if (accounts && accounts.length > 0) {
    const swaps: SwapRecord[] = [];
    let swapIndex = 0;

    for (let di = 0; di < allDefs.length; di++) {
      const def = allDefs[di];
      if (!def) continue;
      await onAction?.(
        `Swap ${String(di + 1)}/${String(allDefs.length)}: ${def.description}`,
      );
      const account = accounts[swapIndex % accounts.length] as Address;
      const isDeployer = account.toLowerCase() === ctx.deployer.toLowerCase();
      const singleDef = [def];

      // Impersonate non-deployer accounts before the swap
      if (!isDeployer) {
        await ctx.request({
          method: "anvil_impersonateAccount",
          params: [account],
        });
      }

      let records: SwapRecord[];
      if (def.protocol === "v2") {
        records = await executeV2Swaps(ctx, deployment, singleDef, account);
      } else if (def.protocol === "v3") {
        records = await executeV3Swaps(ctx, deployment, singleDef, account);
      } else {
        records = await executeV4Swaps(
          ctx,
          deployment,
          tokens,
          singleDef,
          account,
        );
      }

      // Stop impersonating non-deployer accounts after the swap
      if (!isDeployer) {
        await ctx.request({
          method: "anvil_stopImpersonatingAccount",
          params: [account],
        });
      }

      swaps.push(...records);
      swapIndex++;

      // Mine 1-3 blocks every 3rd swap
      if (swapIndex % 3 === 0) {
        const blockCount =
          swapIndex % 9 === 0 ? 3 : swapIndex % 6 === 0 ? 2 : 1;
        await mineBlocks(ctx, blockCount);
      }
    }

    return {
      totalSwaps: swaps.length,
      swaps,
      durationMs: Date.now() - start,
    };
  }

  // Default single-account mode (backward compatible)
  const v2Defs = allDefs.filter((d) => d.protocol === "v2");
  const v3Defs = allDefs.filter((d) => d.protocol === "v3");
  const v4Defs = allDefs.filter((d) => d.protocol === "v4");

  const v2Records = await executeV2Swaps(
    ctx,
    deployment,
    v2Defs,
    undefined,
    onAction,
  );
  const v3Records = await executeV3Swaps(
    ctx,
    deployment,
    v3Defs,
    undefined,
    onAction,
  );
  const v4Records = await executeV4Swaps(
    ctx,
    deployment,
    tokens,
    v4Defs,
    undefined,
    onAction,
  );

  const swaps = [...v2Records, ...v3Records, ...v4Records];

  return {
    totalSwaps: swaps.length,
    swaps,
    durationMs: Date.now() - start,
  };
}
